import React, { useMemo, useEffect, useRef } from "react";
import moment from "moment";
import { Formik, useFormikContext } from "formik";
import { useNavigate } from "react-router";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import DialogContent from "@mui/material/DialogContent";
import { useQuery } from "@apollo/client/react";
import CircularProgress from "@mui/material/CircularProgress";

import { CaptureProvider } from "../../../../../contexts/capture";
import { fetchAssetValues, prepareAssetInputWithCustomFields } from "../../../../../utils/asset";
import { AssetsProvider } from "../../../../../contexts/assets";
import { AssetsCollectionProvider } from "../../../../../contexts/assetsCollection";
import { ProjectInventoryProvider } from "../../../../../contexts/projectInventory";
import {
  ASSET_QUERY,
  REMOVE_ASSET_MUTATION,
  UPDATE_ASSET_MUTATION,
} from "../../../../../constants/graphql";
import { DELETE_SHIPMENT_ITEMS_MUTATION } from "../../../../../constants/graphql/mutations";
import { GET_SHIPMENT_QUERY } from "../../../../../constants/graphql/queries";
import {
  UPDATE_SHIPMENT_ITEMS_MUTATION,
  UPDATE_SHIPMENT_MUTATION,
} from "../../../../../constants/graphql/mutations";

import useMutation from "../../../../../hooks/useMutation";
import { useFieldSettings } from "../../../../../contexts/customFields";
import ConfirmDeleteModal from "../../../../../components/images/ConfirmDeleteModal";
import useQueryParams from "../../../../../hooks/useQueryParams";
import { useItemSchema } from "../useItemSchema";
import FormPage from "./FormPage";
import { getShipmentValues } from "../../../../../utils/shipment";
import { serverFormatSeconds } from "src/utils/dates";
import { addShipmentBillingToAssetInput } from "../../../../../utils/billingHelpers";
import { fullWidthDialog } from "src/constants/dialog";
import omit from "lodash/omit";
import parseErrors, { toString } from "src/utils/parseErrors";
import useNotification from "../../../../../hooks/notification";

const CACHE_EXPIRY_HOURS = 2;
const CACHE_KEY_PREFIX = "item_details_draft_";

const getCacheKey = (assetId, shipmentItemId) => {
  return `${CACHE_KEY_PREFIX}${assetId}_${shipmentItemId}`;
};

const saveDraftToCache = (assetId, shipmentItemId, values) => {
  try {
    const cacheKey = getCacheKey(assetId, shipmentItemId);
    const cacheData = {
      values,
      timestamp: Date.now(),
    };
    sessionStorage.setItem(cacheKey, JSON.stringify(cacheData));
  } catch (error) {
    console.error("Error saving draft to cache:", error);
  }
};

const loadDraftFromCache = (assetId, shipmentItemId) => {
  try {
    const cacheKey = getCacheKey(assetId, shipmentItemId);
    const cached = sessionStorage.getItem(cacheKey);
    if (!cached) return null;
    const cacheData = JSON.parse(cached);
    const expiryTime = CACHE_EXPIRY_HOURS * 60 * 60 * 1000;
    const isExpired = Date.now() - cacheData.timestamp > expiryTime;
    if (isExpired) {
      sessionStorage.removeItem(cacheKey);
      return null;
    }
    return cacheData.values;
  } catch (error) {
    console.error("Error loading draft from cache:", error);
    return null;
  }
};

const clearDraftCache = (assetId, shipmentItemId) => {
  try {
    const cacheKey = getCacheKey(assetId, shipmentItemId);
    sessionStorage.removeItem(cacheKey);
  } catch (error) {
    console.error("Error clearing draft cache:", error);
  }
};

function FormAutoSave({ assetId, shipmentItemId }) {
  const { values, setValues } = useFormikContext();
  const timeoutRef = useRef(null);
  // Restore the draft as edits, not initialValues, so Formik marks it dirty and
  // save paths that skip pristine forms (bulk duplicate) still persist it.
  useEffect(() => {
    const cachedValues = loadDraftFromCache(assetId, shipmentItemId);
    if (cachedValues) {
      setValues(prev => ({ ...prev, ...cachedValues }), false);
    }
  }, [assetId, shipmentItemId]);
  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      saveDraftToCache(assetId, shipmentItemId, values);
    }, 500);
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [values, assetId, shipmentItemId]);
  return null;
}

export default function ItemDetails({
  id,
  project,
  shipment,
  shipmentItem,
  reFetch,
  onClose,
  sameLocation,
}) {
  const params = useQueryParams();
  const navigate = useNavigate();
  const shipmentId = params.get("id");
  const shipmentItemId = params.get("shipmentItemId") || shipmentItem?.id;
  const bulkDuplicateModal = params.get("bulkDuplicateModal");
  const [openRemoveItem, setOpenRemoveItem] = React.useState(false);
  const { notifyError } = useNotification();
  const display = useFieldSettings(project, "receivingSettings");
  const isDraft = shipment.draft || shipment.shipmentStatus === 'draft' || shipment.shipmentStatus === 'onsite_inventory';
  const { data: { asset = {} } = {}, loading } = useQuery(ASSET_QUERY, {
    variables: { id },
  });

  const [updateShipment] = useMutation(UPDATE_SHIPMENT_MUTATION);
  const [updateAsset] = useMutation(UPDATE_ASSET_MUTATION);
  const [removeAsset, { loading: removingAsset }] = useMutation(REMOVE_ASSET_MUTATION, {
    refetchQueries: [{ query: GET_SHIPMENT_QUERY, variables: { id: shipmentId } }],
  });
  const [removeShipmentItem, { loading: removingShipmentItem }] = useMutation(
    DELETE_SHIPMENT_ITEMS_MUTATION,
    {
      refetchQueries: [{ query: GET_SHIPMENT_QUERY, variables: { id: shipmentId } }],
    }
  );
  const [updateShipmentItem] = useMutation(UPDATE_SHIPMENT_ITEMS_MUTATION, {
    refetchQueries: [{ query: GET_SHIPMENT_QUERY, variables: { id: shipmentId } }],
  });
  const itemSchema = useItemSchema(display, project, asset, shipment);
  const validate = isDraft ? undefined : itemSchema;

  const currentShipmentItem =
    shipment?.shipmentItems?.find(item => item.id === shipmentItemId) || shipmentItem;
  const shipmentItemQuantity = currentShipmentItem?.quantity || 0;
  const expectedQuantity = currentShipmentItem?.expectedQuantity;
  const palletsReceivedCount = currentShipmentItem?.palletsReceivedCount;
  const itemNotes = currentShipmentItem?.itemNotes;
  const totalPieces = currentShipmentItem?.totalPieces;

  const initialValues = useMemo(() => {
    return {
      ...fetchAssetValues(asset),
      availableQuantity: asset?.availableQuantity,
      manufacturer: asset?.manufacturer || shipment?.vendorName,
      poNumber: asset?.poNumber || shipment?.customerPoNumber,
      carrierName: asset?.carrierName || shipment?.carrierName,
      orderNumber: asset?.orderNumber || shipment?.orderNumber,
      bolNumber: asset?.bolNumber || shipment?.bol,
      trackingNumber: asset?.trackingNumber || shipment?.trackingNumber,
      truckNumber: asset?.truckNumber || shipment?.truckNumber,
      palletsReceivedCount,
      expectedQuantity,
      shipmentItemQuantity,
      itemNotes,
      totalPieces,
    };
  }, [
    asset,
    shipment?.vendorName,
    shipment?.customerPoNumber,
    shipment?.carrierName,
    shipment?.orderNumber,
    shipment?.bol,
    shipment?.trackingNumber,
    shipment?.truckNumber,
    expectedQuantity,
    shipmentItemQuantity,
    itemNotes,
    totalPieces,
  ]);

  const removing = removingAsset || removingShipmentItem;

  const handleSave = async ({
    attachments,
    availableQuantity,
    shipmentItemQuantity,
    expectedQuantity,
    palletsReceivedCount,
    itemNotes,
    totalPieces,
    ...values
  }) => {
    const rackLocationsChanges = params.get("rackLocationsChanges")
      ? JSON.parse(params.get("rackLocationsChanges"))
      : [];

    const shipmentItemVariables = {
      id: shipmentItemId,
      quantity: shipmentItemQuantity,
      expectedQuantity,
      palletsReceivedCount,
      itemNotes,
      totalPieces,
      ...(values.palletType && { palletType: values.palletType }),
      rackLocations: rackLocationsChanges.length
        ? rackLocationsChanges
        : Array.isArray(currentShipmentItem?.rackLocations) ? currentShipmentItem.rackLocations : [],
      ...(values.dimensions && { dimensions: values.dimensions }),
    };

    // Item edits never change the asset's billing type: it is resolved from the
    // shipment on create, and the shipment's inbound type can be invalid as a
    // storage type, which makes the server reject the whole update.
    const input = omit(
      addShipmentBillingToAssetInput(shipment, prepareAssetInputWithCustomFields(values, asset)),
      "billingType"
    );

    await updateShipmentItem({ variables: shipmentItemVariables });
    const response = await updateAsset({ variables: { id, input } });
    const errors = response?.data?.updateAsset?.errors;
    if (!response || errors?.length) {
      const message = errors?.length ? toString(parseErrors(errors)) : "Failed to save item.";
      notifyError(message);
      throw new Error(message);
    }
    clearDraftCache(id, shipmentItemId);
    if (bulkDuplicateModal !== "true") {
      onClose();
    }

    return response;
  };

  const handleRemoveAsset = async () => {
    if (project.importedAssets) {
      await removeShipmentItem({ variables: { id: shipmentItemId } });
    } else {
      await removeAsset({ variables: { id: asset.id } });
    }
    onClose();
  };

  const handleClose = () => {
    clearDraftCache(id, shipmentItemId);
    const currentParams = new URLSearchParams(window.location.search);
    const currentAddShipmentItem = currentParams.get("addShipmentItem") === "true";

    if (currentAddShipmentItem) {
      toggleRemoveItem();
    } else {
      onClose();
    }
  };

  const toggleRemoveItem = () => setOpenRemoveItem(prev => !prev);

  const isOnsiteInventory = shipment.shipmentStatus === 'onsite_inventory';

  const handleMarkAsReceived = async () => {
    const input = {
      ...getShipmentValues(shipment),
      startTime: moment(new Date()).format(serverFormatSeconds),
      draft: false,
      shipmentStatus: 'in_progress',
    };

    const response = await updateShipment({
      variables: { id: shipmentId, input },
    });
    params.set("shipmentId", response.data.updateShipment.shipment.shipmentId);
    navigate({ search: params.toString() });
  };

  return (
    <Dialog open fullWidth maxWidth="xs" onClose={handleClose} sx={fullWidthDialog}>
      <DialogTitle sx={{ background: "#f4f6f8", display: "flex", justifyContent: "space-between" }}>
        <Box width="48px" />
        <Typography fontSize={24} fontWeight={500} color="textPrimary">
          Item Details
        </Typography>
        <IconButton onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ background: "#f4f6f8" }}>
        {loading ? (
          <Box height={258} display="flex" justifyContent="center" alignItems="center">
            <CircularProgress color="primary" />
          </Box>
        ) : (
          <Box>
            <CaptureProvider>
              <ProjectInventoryProvider>
                <AssetsCollectionProvider variables={{ projectId: project?.id }} skipQuery>
                  <AssetsProvider>
                    <Formik
                      key={asset?.id}
                      initialValues={initialValues}
                      onSubmit={handleSave}
                      validate={validate}
                    >
                      <>
                        <FormAutoSave assetId={id} shipmentItemId={shipmentItemId} />
                        <FormPage
                          type="item"
                          shipmentId={shipment.id}
                          shipment={shipment}
                          currentAsset={{
                            ...asset,
                            images: project.addPreviouslyReceivedAssetsToShipments || project?.settings?.onsite_capture_to_warehouse_storage
                              ? asset.images
                              : currentShipmentItem?.attachments
                          }}
                          onCloseModal={onClose}
                          handeRefetch={reFetch}
                          shipmentItem={currentShipmentItem}
                          shipmentItemId={shipmentItemId}
                          removing={removing}
                          toggleRemoveItem={toggleRemoveItem}
                          isDraft={isDraft}
                          isActualDraft={shipment.draft || shipment.shipmentStatus === 'draft'}
                          isOnsiteInventory={isOnsiteInventory}
                          onMarkAsReceived={handleMarkAsReceived}
                          sameLocation={sameLocation}
                          project={project}
                          displayType="receivingSettings"
                          closeItemDetails={handleClose}
                        />
                      </>
                    </Formik>
                  </AssetsProvider>
                </AssetsCollectionProvider>
              </ProjectInventoryProvider>
            </CaptureProvider>
          </Box>
        )}
      </DialogContent>
      {openRemoveItem && (
        <ConfirmDeleteModal
          title="Delete Item"
          descr="Are you sure want to delete the selected item?"
          onClose={toggleRemoveItem}
          onDelete={handleRemoveAsset}
        />
      )}
    </Dialog>
  );
}
